import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAssistApi } from "./AppAssistApi";
import { AppAssistRich } from "./AppAssistRich";

// AppAssistStream - the AI panel's STREAMED answer and its WARM-UP ping: the rules both halves share.
//
// ==================================================================================================
// !! WHY (owner, 2026-09-27: "A response slower than 3 seconds is not acceptable")
// ==================================================================================================
//
// The panel showed nothing until the whole answer was written (~20 s). Now `/assist` answers a question
// that carries `stream: true` as NDJSON - one JSON object per line: `{"t":"start"}`, then `{"t":"text",
// "d":"..."}` pieces as the model writes, then the FINAL line, which is exactly today's one-object answer
// (`ok`, `answer`, `draft`, `history`...). A refusal is still ONE plain JSON object, no stream. The page
// shows the pieces as they come and replaces them with the final answer.
//
// ⛔ WHAT A PIECE MAY CARRY - `Visible`, applied by the SERVICE before it writes a piece and again by the
// PAGE before it draws one (two guards, one rule):
//   - NOTHING FROM THE `===DRAFT===` LINE ON (decision 9: the sendable text, its card and its Send appear
//     only with the final answer, from `AssistRules.Split`) - and a last line that could still BECOME that
//     marker is held back until it is known not to;
//   - NO ```wac-html FENCE CONTENT - a table or chart is ONE `Placeholder` line in the text; a line that
//     could still become the fence's opening is held back too.
// ⭐ TABLES AND CHARTS DRAWN AS SOON AS THEY ARE WRITTEN (owner-approved, 2026-09-27: a chart answer takes
// ~50-60 s). Beside the text pieces the service writes `{"t":"rich","i":N,...}` events (`RichLine`): the
// N-th fence's kind as soon as its first bytes say it (the page shows "Drawing a chart…" / "Building a
// table…" with moving dots, never a percentage), then - the moment its closing line has come - the block
// CLEANED BY THE SERVICE EXACTLY AS THE FINAL ANSWER WILL CARRY IT (`AssistRichStream`: same allow-list,
// same budgets, same notes). ⛔ The raw fence never travels: only that cleaned markup, which the page then
// cleans AGAIN with its own per-block id prefix and BUILDS element by element (`AppAssistPanel.Rich`, the
// final answer's own path). The final answer still replaces the whole streamed view; slot N of the stream
// and fence N of the final answer are the same block, drawn by the same code from the same cleaned markup.
// ⚠ A slot is a `Partial` line that is exactly `Placeholder` - a model that wrote that very line itself
// would shift the slots until the final answer (a cosmetic, cleaned-either-way edge).
// ⚠ `Visible` only ever GROWS as the text grows (what was shown is never taken back), so a piece is always
// "what `Visible` says now, minus what was already sent".
// ⭐ THE WARM-UP (owner, same day: "do that automatically when a person page is opened. Do NOT wait for the
// first chat message"): an OWNER-LEVEL `action: "warm"` - no model call, no daily count - that starts
// Claude before a question exists. `page` is optional: with it, the service also reads that chat's
// history into memory. When the app sends it: `AppAssistWarm`.
// ⚠ Pure: no DOM, no transport - the Bridge suite imports it (`AssistStream.test.ts`).

/**
 * ⭐ ONE TABLE OR CHART OF A STREAMED ANSWER (owner-approved 2026-09-27: "chart answers take ~50-60 s - make
 * them FEEL fast"). The `Index`-th ```wac-html fence of the answer (0 = the first `Placeholder` line of
 * `Partial`). While it is written: its `Kind` as soon as the first bytes say it ("chart" for `<svg`, "table"
 * for `<table`, "" not yet known). Once its closing line has come (`Done`): the block ALREADY CLEANED BY THE
 * SERVICE exactly as the final answer will carry it (`AssistRichStream`) - `Html` (one line, drawn by the
 * page through the SAME `AppAssistPanel.Rich` path, cleaned again there) or the `Note` shown instead, or
 * both "" (the final answer will not carry it at all).
 */
export class AppAssistStreamRich {
    public Index: number = 0;
    public Kind: string = "";
    public Html: string = "";
    public Note: string = "";
    public Done: boolean = false;
}

/** One stretch of a streamed answer as the panel draws it: words, or the slot of a table/chart. */
export class AppAssistStreamPart {
    /** "text" or "slot". */
    public Kind: string = "text";
    public Text: string = "";
    /** The slot's state (`Kind` "slot" only). */
    public Rich: AppAssistStreamRich = new AppAssistStreamRich();
}

/** What has arrived of a streamed answer. */
export class AppAssistStreamRead {
    /** The answer's words so far - already `Visible`. */
    public Partial: string = "";
    /** The final JSON answer (or the one-object refusal), "" while it has not arrived. */
    public Final: string = "";
    public Started: boolean = false;
    /** ⭐ The tables and charts heard of so far, by `Index` (a later event for one index replaces it). */
    public Rich: AppAssistStreamRich[] = [];
}

export class AppAssistStream {
    /** The request field that asks for a stream. ⛔ Must match `AssistRules.AskIn`. */
    public static readonly StreamField: string = "stream";
    /** ⛔ Must match `AssistRules.WarmAction`. */
    public static readonly WarmAction: string = "warm";
    /** The content type of a streamed answer. */
    public static readonly ContentType: string = "application/x-ndjson; charset=utf-8";
    /** What a table or chart shows while the answer is still being written. */
    public static readonly Placeholder: string = "[table or chart - shown when the answer is complete]";

    /**
     * The part of `Text` a piece may show - see the header. Lines are kept whole except the last one, which
     * is shown as far as it has been written unless it could still become the marker or a fence opening.
     */
    public static Visible = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Lines: string[] = nz(Text).replace(/\r/g, "").split("\n");
            const Out: string[] = [];
            let InFence: boolean = false;
            for (let At: number = 0; At < Lines.length; At = At + 1) {
                const Done: boolean = At < Lines.length - 1;
                const Seen: string = AppAssistStream.LineOf(Lines[At], Done, InFence);
                if (Seen === AppAssistStream.Stop) { break; }
                if (Seen === AppAssistStream.Enter) { InFence = true; Out.push(AppAssistStream.Placeholder + "\n"); continue; }
                if (Seen === AppAssistStream.Leave) { InFence = false; continue; }
                Out.push(Seen);
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.Visible");
            return "";
        }
    };

    private static readonly Stop: string = "\u0000stop";
    private static readonly Enter: string = "\u0000enter";
    private static readonly Leave: string = "\u0000leave";

    /** One line: what it adds ("" for nothing), or `Stop` / `Enter` / `Leave`. `Done` = its newline has come. */
    private static LineOf = (Line: string, Done: boolean, InFence: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistStream.Stop; }
        try {
            const Trim: string = Line.trim();
            const Low: string = Trim.toLowerCase();
            const Open: string = "```" + "wac-html";
            if (InFence === true) {
                if (Done === true && Trim === "```") { return AppAssistStream.Leave; }
                return "";
            }
            if (Trim.indexOf(AppAssistApi.DraftMarker) === 0) { return AppAssistStream.Stop; }
            if (Low.indexOf("```wac") === 0) { return AppAssistStream.OpenOrHold(Done); }
            if (Done === false && (Trim.length === 0 || AppAssistApi.DraftMarker.indexOf(Trim) === 0 || Open.indexOf(Low) === 0)) {
                return AppAssistStream.Stop;
            }
            if (Done === true) { return Line + "\n"; }
            return Line;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.LineOf");
            return AppAssistStream.Stop;
        }
    };

    /** A fence opening: entered once its line is whole; until then nothing more is shown. */
    private static OpenOrHold = (Done: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistStream.Stop; }
        try {
            if (Done === true) { return AppAssistStream.Enter; }
            return AppAssistStream.Stop;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.OpenOrHold");
            return AppAssistStream.Stop;
        }
    };

    /**
     * What has arrived in `Body` (the response text so far). `Ended` = the response is complete, so a last
     * line with no newline counts. ⚠ A body that is ONE JSON object is a refusal (or an older service's
     * whole answer) - `Final` is then the body itself.
     */
    public static Read = (Body: string, Ended: boolean): AppAssistStreamRead => {
        if (getCoreUtils().KErrorMode === true) { return new AppAssistStreamRead(); }
        try {
            const Out: AppAssistStreamRead = new AppAssistStreamRead();
            const Text: string = nz(Body);
            const Lines: string[] = Text.split("\n");
            let Whole: number = Lines.length - 1;
            if (Ended === true) { Whole = Lines.length; }
            for (let At: number = 0; At < Whole; At = At + 1) { AppAssistStream.Take(Lines[At], Out); }
            if (Ended === true && Out.Final.length === 0 && Out.Started === false) { Out.Final = Text; }
            Out.Partial = AppAssistStream.Visible(Out.Partial);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.Read");
            return new AppAssistStreamRead();
        }
    };

    /** One whole line of the stream into `Out`. DOCUMENTED EXCEPTION: pure parse of one line.
     *  ERROR-SUPPRESSED-JUSTIFIED: a line that is not JSON (a proxy's page) adds nothing; the final answer's
     *  absence is then read as "our end" by `AppAssistApi.Parse`. */
    private static Take = (Line: string, Out: AppAssistStreamRead): void => {
        try {
            if (Line.trim().length === 0) { return; }
            const Raw: unknown = JSON.parse(Line);
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (Doc.t === "start") { Out.Started = true; return; }
            if (Doc.t === "text") { if (typeof Doc.d === "string") { Out.Partial = Out.Partial + (Doc.d as string); } return; }
            if (Doc.t === AppAssistStream.RichEvent) { AppAssistStream.Keep(Out, AppAssistStream.RichOf(Doc)); return; }
            if (Doc.t === undefined) { Out.Final = Line; }
        } catch (e) {
            return;
        }
    };

    // ── Tables and charts while the answer is written (2026-09-27) ────────────────────────────────

    /** The event kind of a table/chart line. ⛔ Written only by `RichLine`. */
    public static readonly RichEvent: string = "rich";
    /** A stream carries at most this many table/chart slots; a higher index is ignored. */
    public static readonly MaxSlots: number = 64;
    public static readonly ChartLabel: string = "Drawing a chart…";
    public static readonly TableLabel: string = "Building a table…";
    public static readonly EitherLabel: string = "Drawing a chart or table…";

    /**
     * "chart" / "table" from the first tag of a fence's body so far - `<svg` or `<table` followed by a space,
     * `>` or `/` - else "" (not yet known). ⚠ Only the FIRST of the two counts: a table inside a chart's
     * `<foreignObject>` is still a chart (and is cleaned away anyway).
     */
    public static KindSoFar = (Body: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Found: RegExpExecArray | null = /<(svg|table)[\s>\/]/i.exec(nz(Body));
            if (Found === null) { return ""; }
            if (Found[1].toLowerCase() === "table") { return "table"; }
            return "chart";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.KindSoFar");
            return "";
        }
    };

    /** What a slot says while its block is still being written. */
    public static LabelFor = (Kind: string): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistStream.EitherLabel; }
        try {
            if (Kind === "chart") { return AppAssistStream.ChartLabel; }
            if (Kind === "table") { return AppAssistStream.TableLabel; }
            return AppAssistStream.EitherLabel;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.LabelFor");
            return AppAssistStream.EitherLabel;
        }
    };

    /**
     * ⛔ THE ONE WRITER OF A TABLE/CHART EVENT (the service's): `{"t":"rich","i":N,"k":kind}` while it is
     * written, `{..., "done":true, "h":cleaned, "n":note}` once it closed. `Html` must already be cleaned.
     */
    public static RichLine = (Rich: AppAssistStreamRich): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Doc: Record<string, unknown> = { t: AppAssistStream.RichEvent, i: Rich.Index, k: Rich.Kind };
            if (Rich.Done === true) { Doc.done = true; Doc.h = Rich.Html; Doc.n = Rich.Note; }
            return JSON.stringify(Doc) + "\n";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.RichLine");
            return "";
        }
    };

    /**
     * A table/chart event read back, or null when any field is not what `RichLine` writes. ⛔ The note can
     * only be one of the two known sentences, and the markup is one line inside the rich budget - the page
     * never shows a string the service did not mean as one of these.
     */
    public static RichOf = (Doc: Record<string, unknown>): AppAssistStreamRich | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Out: AppAssistStreamRich = new AppAssistStreamRich();
            if (typeof Doc.i !== "number" || Number.isInteger(Doc.i) !== true || (Doc.i as number) < 0 || (Doc.i as number) >= AppAssistStream.MaxSlots) { return null; }
            if (Doc.k !== "" && Doc.k !== "chart" && Doc.k !== "table") { return null; }
            Out.Index = Doc.i as number;
            Out.Kind = Doc.k as string;
            if (Doc.done !== true) { return Out; }
            if (typeof Doc.h !== "string" || (Doc.h as string).length > AppAssistRich.RichMaxChars || /[\r\n]/.test(Doc.h as string) === true) { return null; }
            if (Doc.n !== "" && Doc.n !== AppAssistRich.TooLargeNote && Doc.n !== AppAssistRich.DroppedNote) { return null; }
            Out.Done = true;
            Out.Html = Doc.h as string;
            Out.Note = Doc.n as string;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.RichOf");
            return null;
        }
    };

    /** `One` into `Out.Rich`, replacing an earlier event for its index; a done block is never taken back. */
    private static Keep = (Out: AppAssistStreamRead, One: AppAssistStreamRich | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (One === null) { return; }
            for (let At: number = 0; At < Out.Rich.length; At = At + 1) {
                if (Out.Rich[At].Index !== One.Index) { continue; }
                if (Out.Rich[At].Done !== true) { Out.Rich[At] = One; }
                return;
            }
            Out.Rich.push(One);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.Keep");
        }
    };

    /** A short fingerprint of what the slots say - so a new event is drawn even when no word came with it. */
    public static RichSig = (Rich: AppAssistStreamRich[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (const One of Rich) { Out.push(String(One.Index) + One.Kind + String(One.Done) + String(One.Html.length) + One.Note); }
            return Out.join("|");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.RichSig");
            return "";
        }
    };

    /**
     * The streamed answer as the panel draws it: words, and a slot wherever a line is exactly `Placeholder`
     * (the N-th such line is slot N, with what `Rich` says of index N - or a still-unknown one).
     */
    public static Parts = (Partial: string, Rich: AppAssistStreamRich[]): AppAssistStreamPart[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppAssistStreamPart[] = [];
            let Words: string[] = [];
            let Slot: number = 0;
            for (const Line of nz(Partial).split("\n")) {
                if (Line.trim() !== AppAssistStream.Placeholder) { Words.push(Line); continue; }
                if (Words.join("").trim().length > 0) { Out.push(AppAssistStream.PartOf("text", Words.join("\n"), new AppAssistStreamRich())); }
                Words = [];
                Out.push(AppAssistStream.PartOf("slot", "", AppAssistStream.SlotOf(Rich, Slot)));
                Slot = Slot + 1;
            }
            if (Words.join("").trim().length > 0) { Out.push(AppAssistStream.PartOf("text", Words.join("\n"), new AppAssistStreamRich())); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.Parts");
            return [];
        }
    };

    /** What `Rich` says of slot `Index`, or a still-unknown one. */
    private static SlotOf = (Rich: AppAssistStreamRich[], Index: number): AppAssistStreamRich => {
        if (getCoreUtils().KErrorMode === true) { return new AppAssistStreamRich(); }
        try {
            for (const One of Rich) { if (One.Index === Index) { return One; } }
            const Out: AppAssistStreamRich = new AppAssistStreamRich();
            Out.Index = Index;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.SlotOf");
            return new AppAssistStreamRich();
        }
    };

    /** DOCUMENTED EXCEPTION: builds a plain value. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static PartOf = (Kind: string, Text: string, Rich: AppAssistStreamRich): AppAssistStreamPart => {
        const Out: AppAssistStreamPart = new AppAssistStreamPart();
        Out.Kind = Kind;
        Out.Text = Text;
        Out.Rich = Rich;
        return Out;
    };

    /** The warm-up body - owner-level; `Page` may be "". "" when there is no proof to send. */
    public static WarmBody = (Phone: string, Key: string, Page: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Proof: Record<string, unknown> | null = AppAssistApi.ProofFields(Phone, Key, Page, AppAssistStream.WarmAction, false);
            if (Proof === null) { return ""; }
            return JSON.stringify(Proof);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.WarmBody");
            return "";
        }
    };

    /** A question body with `stream: true` added. "" stays "". */
    public static Streamed = (Body: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Body).length === 0) { return ""; }
            const Doc: Record<string, unknown> = JSON.parse(Body) as Record<string, unknown>;
            Doc[AppAssistStream.StreamField] = true;
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistStream.Streamed");
            return "";
        }
    };
}
